import styled, { DefaultTheme, useTheme } from 'styled-components';
import { Spinner } from './spinner';
import { addAlpha } from '@algoan/deep-dive/theme';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * choose between the three variants of the button that we use on Dashboard
   * see Storybook for more details
   */
  variant?: 'primary' | 'secondary' | 'tertiary';
  /**
   * choose whether the button take up the entire width of the parent container, or have a fixed size
   */
  fullWidth?: boolean;
  /**
   * add an icon situated to the right of the text
   */
  icon?: string | React.FC<{ color?: string }>;
  /**
   * if true, the text and the icon are replaced by a spinner animation
   */
  loading?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  fullWidth,
  icon,
  children,
  loading,
  ...buttonAttributes
}) => {
  const theme = useTheme();

  const getIcon = (): React.ReactNode => {
    if (loading) {
      return <S.LoadingIcon />;
    }

    if (!icon) {
      return null;
    }

    if (typeof icon === 'string') {
      // eslint-disable-next-line @next/next/no-img-element
      return <img src={icon} />;
    } else {
      const Icon = icon;
      return <Icon color={getColorFromVariant(variant, buttonAttributes.disabled ?? false, theme)} />;
    }
  };

  const iconComponent = getIcon();
  const showChildrenWhenLoading = !(!icon && loading);

  return (
    <S.Container variant={variant} fullWidth={fullWidth} $loading={loading} {...buttonAttributes}>
      {iconComponent && (
        <S.IconContainer id="icon-container" showChildrenWhenLoading={showChildrenWhenLoading} variant={variant}>
          {iconComponent}
        </S.IconContainer>
      )}
      {showChildrenWhenLoading && children}
    </S.Container>
  );
};

const getColorFromVariant = (
  variant: ButtonProps['variant'],
  disabled: boolean,
  theme: DefaultTheme,
): string | undefined => {
  switch (variant) {
    case 'primary':
      return 'white';
    case 'secondary':
      return disabled ? addAlpha(theme.secondary.softBlue2, 0.35) : theme.secondary.softBlue2;
    case 'tertiary':
      return theme.legacy.secondary;
  }
};

const S = {
  Container: styled.button<{
    variant: ButtonProps['variant'];
    fullWidth: ButtonProps['fullWidth'];
    $loading?: boolean;
  }>`
    display: flex;
    position: relative;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    cursor: pointer;
    outline: none;
    text-transform: uppercase;
    box-sizing: border-box;
    ${({ fullWidth }) => (fullWidth ? 'width: 100%;' : '')}
    ${({ $loading }) => ($loading ? 'pointer-events: none;' : '')}

    &[disabled] {
      pointer-events: none;
    }

    ${({ variant, theme, disabled = false }) => {
      const color = getColorFromVariant(variant, disabled, theme);
      switch (variant) {
        case 'primary':
          return `
          background: ${theme.algoanBlue};
          border-width: 0;
          color: ${color};
          font-size: 14px;
          height: 45px;
          line-height: 47px;
          padding: 0px 16px;

          &:hover {
            box-shadow: 0px 3px 15px rgba(0, 70, 107, 0.35);
          }

          &[disabled] {
            background: ${theme.algoanBlue}59;
            color: rgba(255, 255, 255, 0.6);
          }
          `;

        case 'secondary':
          return `
          border: 1px solid ${color};
          color: ${color};
          font-size: 12px;
          background: white;
          height: 32px;
          padding: 0px 16px;

          &:hover {
            box-shadow: 0px 3px 15px rgba(0, 70, 107, 0.35);
            background: ${theme.secondary.softBlue2};
            cursor: pointer;
            color: ${theme.white};
            box-shadow: 0px 3px 15px rgba(0, 70, 107, 0.35);
          }

          &:active {
            box-shadow: unset;
          }

          &:hover #icon-container {
            & > svg {
              path {
                fill: ${theme.white};
              }
            }
          }
            `;
        case 'tertiary': {
          const borderThickness = 1;
          return `

            border: ${borderThickness}px solid ${theme.secondary.softBlue2};
            color: ${theme.secondary.softBlue2};
            background: white;
            font-size: 14px;
            line-height: 15px;
            padding: 7.5px 16px;

            &:hover {
              box-shadow: 0px 3px 15px rgba(0, 70, 107, 0.35);
            }

            &[disabled]::before {
              content: '';
              position: absolute;
              width: calc(100% + ${2 * borderThickness}px);
              height: calc(100% + ${2 * borderThickness}px);
              opacity: 0.7;
              background: white;
            }
          `;
        }
      }
    }}
  `,
  IconContainer: styled.div<{ variant: ButtonProps['variant']; showChildrenWhenLoading: boolean }>`
    height: 14px;
    margin-right: ${({ variant }) => {
      switch (variant) {
        case 'primary':
        case 'secondary':
          return '8px';
        case 'tertiary':
          return '6px';
      }
    }};

    margin-right: ${({ showChildrenWhenLoading }) => (!showChildrenWhenLoading ? '0' : '')};

    & > img {
      height: 14px;
      width: auto;
      vertical-align: middle;
    }

    & > svg {
      height: 100%;
    }
  `,
  LoadingIcon: styled(Spinner)``,
};
